import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Icons} from '../../assets';
import {ROUTES} from '../../constants/routes';
import {useAuth} from '../../contexts/auth.provider';
import {OrganizationServiceImpl} from '../../services';

enum SidebarGroup {
  MAIN = 'main',
  SECONDARY = 'secondary',
}

interface SidebarItem {
  id: string;
  label: string;
  path: string;
  filledIcon: string;
  outlineIcon: string;
  roles?: string[];
  group?: SidebarGroup;
}

interface SidebarConfig {
  navigationItems: SidebarItem[];
  bottomItems: SidebarItem[];
}

const DEFAULT_SIDEBAR_CONFIG: SidebarConfig = {
  navigationItems: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: ROUTES.DASHBOARD,
      filledIcon: Icons.HOME_FILLED,
      outlineIcon: Icons.HOME_OUTLINE,
      group: SidebarGroup.MAIN,
    },
    {
      id: 'search-candidates',
      label: 'Search Candidates',
      path: ROUTES.SEARCH_CANDIDATES,
      filledIcon: Icons.FILLED_MAGNIFYING_GLASS,
      outlineIcon: Icons.MAGNIFYING_GLASS_BLACK,
      group: SidebarGroup.MAIN,
    },
  ],
  bottomItems: [
    {
      id: 'settings',
      label: 'Settings',
      path: ROUTES.SETTINGS,
      filledIcon: Icons.ACTIVE_SETTINGS_GEAR,
      outlineIcon: Icons.SETTINGS_GEAR,
      group: SidebarGroup.SECONDARY,
    },
  ],
};

interface DashboardSidebarProps {
  config?: SidebarConfig;
  userRoles?: string[];
  onLogout?: () => void;
  userAvatar?: string;
  collapsed?: boolean;
}

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  config = DEFAULT_SIDEBAR_CONFIG,
  userRoles = [],
  onLogout,
  userAvatar,
  collapsed = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {logout, user, getProfileImage, fetchAccountDetails, organizationInfo} = useAuth();

  // Local state to store org logo
  const [orgLogo, setOrgLogo] = useState<string | null>(null);

  // Organization service instance
  const organizationService = useMemo(() => new OrganizationServiceImpl(), []);

  // Fetch org details after account details load
  useEffect(() => {
    const fetchOrgDetails = async () => {
      try {
        await fetchAccountDetails(); // make sure user/org info is loaded first

        const organization = organizationInfo?.organization;
        if (organization?.id) {
          const orgData = await organizationService.getOrganizationById(organization.id);
          if (orgData.logoImg) {
            setOrgLogo(orgData.logoImg.url);
          }
        }
      } catch (err) {
        console.error('Error fetching organization details:', err);
      }
    };

    fetchOrgDetails();
  }, [fetchAccountDetails, organizationInfo, organizationService]);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      logout();
      navigate('/login');
    }
  };

  const getAvatarSource = () => {
    if (userAvatar) return userAvatar;
    const profileImage = getProfileImage();
    if (profileImage) return profileImage;
    return Icons.USER_AVATAR;
  };

  const isItemActive = (path: string) => location.pathname === path;

  const filterItemsByRole = (items: SidebarItem[]) => {
    return items.filter(item => {
      if (!item.roles || item.roles.length === 0) return true;
      return item.roles.some(role => userRoles.includes(role));
    });
  };

  const visibleNavItems = filterItemsByRole(config.navigationItems);
  const visibleBottomItems = filterItemsByRole(config.bottomItems);

  console.log('visibleNavItems', visibleNavItems);

  return (
    <div
      className={`h-screen pb-5 bg-gray-100 fixed left-0 top-0 flex flex-col justify-between items-center z-50 ${
        collapsed ? 'w-16' : 'w-20'
      } transition-all duration-200`}>
      <div className="flex-1 flex flex-col justify-start items-center gap-6">
        <div className="w-14 h-14 p-2.5 flex justify-center items-center">
          <img
            className="w-12 h-12 object-contain"
            src={orgLogo || Icons.DEFAULT_LOGO}
            alt={organizationInfo?.organization?.name || 'Recco Logo'}
          />
        </div>

        <div className="flex flex-col justify-start items-start gap-4">
          {visibleNavItems.map(item => (
            <button key={item.id} onClick={() => navigate(item.path)} title={item.label} className="transition-opacity hover:opacity-70">
              <img src={isItemActive(item.path) ? item.filledIcon : item.outlineIcon} alt={item.label} />
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col justify-start items-center gap-4">
        {visibleBottomItems.map(item => (
          <button key={item.id} onClick={() => navigate(item.path)} title={item.label} className="transition-opacity hover:opacity-70">
            <img src={isItemActive(item.path) ? item.filledIcon : item.outlineIcon} alt={item.label} />
          </button>
        ))}

        <button onClick={handleLogout} title="Sign Out" className="transition-opacity hover:opacity-70">
          <img src={Icons.LOGOUT_SIGN_OUT} alt="Sign Out" />
        </button>
      </div>
    </div>
  );
};
